import { useEffect, useState } from "react";
import type { PublicParentOpinion } from "@fa/shared";
import { t as getFixedT } from "@/i18n";
import { directionFor, type SupportedLanguage } from "@/lib/site";
import { Carousel } from "@/components/Carousel";
import { getPublicParentOpinions } from "@/api/publicApi";
import "./ParentsOpinions.css";

interface ParentOpinion {
  name: string;
  role: string;
  quote: string;
}

interface ParentsOpinionsProps {
  lang: SupportedLanguage;
}

/**
 * #parents-opinions — an additional, parent-voiced section distinct from `#testimonials` (which
 * currently ships a generic "coming soon" placeholder per 09-landing-page.md §9 assumption #2).
 * This section carries real, attributed-sounding parent quotes (name + relationship line + a short
 * opinion). The prerendered/no-JS baseline is the `parentsOpinions.items` i18n copy (unchanged
 * markup: each card is `<blockquote>`/`<cite>`, the avatar initial is decorative `aria-hidden`) —
 * on mount, T141 client-fetches the live `@Public` list (`GET /public/content/parent-opinions`,
 * both languages per row per DEC-WC6) and swaps the carousel's data source to it once it resolves
 * non-empty, picking the active `lang` client-side. Any failure, empty result, or the window before
 * the fetch resolves all keep rendering the i18n fallback — the section is never empty and the
 * prerendered HTML is unaffected (DEC-WC4/FR-16/FR-17).
 *
 * Presented as a `Carousel` (T-polish pass): every card is still a plain list item rendered by the
 * server/prerender pass (see `Carousel.tsx`'s doc comment) — only the slide-by-slide navigation
 * chrome is a client-side enhancement on top of a native, no-JS-required scroll-snap list. Swapping
 * the underlying `slides` array after the fetch resolves does not change the carousel's markup,
 * a11y wiring, or autoplay/keyboard behavior — only which `ParentOpinion[]` it renders.
 */
export function ParentsOpinions({ lang }: ParentsOpinionsProps) {
  const t = getFixedT(lang);
  const dir = directionFor(lang);
  const fallbackItems = t("parentsOpinions.items", {
    returnObjects: true,
  }) as ParentOpinion[];

  const [liveOpinions, setLiveOpinions] = useState<PublicParentOpinion[]>([]);
  const [status, setStatus] = useState<"loading" | "loaded" | "error">(
    "loading",
  );

  useEffect(() => {
    let cancelled = false;
    getPublicParentOpinions()
      .then((data) => {
        if (cancelled) return;
        setLiveOpinions(data);
        setStatus("loaded");
      })
      .catch(() => {
        if (cancelled) return;
        setStatus("error");
      });
    // Mount-only fetch, same pattern as `LeadForm`'s grade/school lookups — no retry UI here since
    // a failure silently keeps the i18n fallback (there is nothing for a visitor to "fix").
    return () => {
      cancelled = true;
    };
  }, []);

  // Live data only replaces the fallback once it has actually resolved with at least one row;
  // loading/error/empty all keep the prerendered i18n copy so the section is never empty and never
  // flickers to a blank state.
  const items: ParentOpinion[] =
    status === "loaded" && liveOpinions.length > 0
      ? liveOpinions.map((opinion) => ({
          name: lang === "ar" ? opinion.nameAr : opinion.nameEn,
          role: lang === "ar" ? opinion.roleAr : opinion.roleEn,
          quote: lang === "ar" ? opinion.quoteAr : opinion.quoteEn,
        }))
      : fallbackItems;

  return (
    <section
      id="parents-opinions"
      className="fa-parents-opinions"
      aria-labelledby="parents-opinions-heading"
    >
      <div className="fa-section-inner">
        <h2 id="parents-opinions-heading" className="fa-section-heading">
          {t("parentsOpinions.heading")}
        </h2>
        <p className="fa-parents-opinions__subtitle">
          {t("parentsOpinions.subtitle")}
        </p>
        <Carousel
          dir={dir}
          ariaLabel={t("parentsOpinions.carousel.label")}
          prevLabel={t("parentsOpinions.carousel.prev")}
          nextLabel={t("parentsOpinions.carousel.next")}
          slideLabel={(index, total) =>
            t("parentsOpinions.carousel.slide", { index, total })
          }
          goToLabel={(index, total) =>
            t("parentsOpinions.carousel.goTo", { index, total })
          }
          autoplayMs={7000}
          slides={items.map((item, index) => ({
            // Position-based key: parent names are free text, so an admin can legitimately add two
            // opinions from parents with the same name — keying off the name would collide and
            // break the carousel's slide/dot reconciliation.
            id: `opinion-${index}`,
            content: (
              <figure className="fa-parents-opinions__card">
                <span
                  className="fa-parents-opinions__avatar"
                  aria-hidden="true"
                >
                  {item.name.trim().charAt(0)}
                </span>
                <blockquote className="fa-parents-opinions__quote">
                  <p>{item.quote}</p>
                </blockquote>
                <figcaption className="fa-parents-opinions__meta">
                  <cite className="fa-parents-opinions__name">
                    {item.name}
                  </cite>
                  <span className="fa-parents-opinions__role">
                    {item.role}
                  </span>
                </figcaption>
              </figure>
            ),
          }))}
        />
      </div>
    </section>
  );
}
